import { NotificationApi } from "@/api";
import i18n from "@/i18n";
import Router from "@/router";
import { notificationCopyKey } from "@/utilities/notificationCopy";

const getDefaultState = () => {
  return {
    items: [],
    unreadCount: 0,
    connectionStatus: "disconnected", // disconnected | connecting | connected
  };
};

// Normalizza l'evento SignalR (campi flat) nella stessa shape degli item REST
function eventToItem(payload) {
  return {
    id: payload.notificationId,
    type: payload.type,
    dsrQuestionnaireId: payload.dsrQuestionnaireId,
    dsrUploadedDocumentId: payload.dsrUploadedDocumentId,
    payload: {
      fileName: payload.fileName,
      fileType: payload.fileType,
      errorShort: payload.errorShort,
      dsrName: payload.dsrName,
      year: payload.year,
      detectedPeriod: payload.detectedPeriod,
    },
    createdAt: payload.createdAt,
    isRead: false,
  };
}

function navigateToDsr(dsrQuestionnaireId) {
  const current = Router.currentRoute;
  if (
    current.name === "dsr.info.index" &&
    Number(current.params.id) === Number(dsrQuestionnaireId)
  )
    return;
  Router.push({
    name: "dsr.info.index",
    params: { id: String(dsrQuestionnaireId) },
  });
}

// Throttling dei toast: lo snackbar ha un solo slot, quindi una raffica di notifiche si
// sovrascriverebbe mostrando solo l'ultima. Il primo evento della finestra mostra il toast
// specifico; gli eventi successivi entro la finestra vengono accorpati in un unico toast
// riepilogativo. Stato transiente fuori dallo store (non reattivo, non persistito).
const TOAST_BURST_WINDOW_MS = 1500;
let toastWindowTimer = null;
let toastBurstExtra = 0;

function showFailureToast(dispatch, item) {
  dispatch(
    "snackbar/setSnackbar",
    {
      timeout: 8000,
      color: "error",
      message: i18n.t(notificationCopyKey(item.type, "title"), {
        fileName: item.payload.fileName,
      }),
      action: {
        label: i18n.t("notifications.toast-open"),
        handler: () => {
          dispatch("markAsRead", item.id);
          navigateToDsr(item.dsrQuestionnaireId);
        },
      },
    },
    { root: true }
  );
}

function showBurstSummaryToast(dispatch, extraCount) {
  dispatch(
    "snackbar/setSnackbar",
    {
      timeout: 8000,
      color: "error",
      message: i18n.t("notifications.documents-failed-summary", {
        n: extraCount,
      }),
    },
    { root: true }
  );
}

export default {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    getItems: (state) => state.items,
    getUnreadCount: (state) => state.unreadCount,
    getConnectionStatus: (state) => state.connectionStatus,
  },
  mutations: {
    SET_ITEMS(state, items) {
      state.items = items;
    },
    PREPEND_ITEM(state, item) {
      state.items.unshift(item);
    },
    SET_UNREAD_COUNT(state, count) {
      state.unreadCount = count;
    },
    MARK_READ(state, notificationId) {
      const item = state.items.find((i) => i.id === notificationId);
      if (item && !item.isRead) {
        item.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    MARK_ALL_READ(state) {
      state.items.forEach((item) => {
        item.isRead = true;
      });
      state.unreadCount = 0;
    },
    SET_CONNECTION_STATUS(state, status) {
      state.connectionStatus = status;
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    async fetchInitial({ commit }) {
      const [listResponse, countResponse] = await Promise.all([
        NotificationApi.getAll(),
        NotificationApi.getUnreadCount(),
      ]);
      commit("SET_ITEMS", listResponse.data.items);
      commit("SET_UNREAD_COUNT", countResponse.data.count);
    },
    onIncoming({ commit, state, dispatch }, payload) {
      // Dedup: più tab/riconnessioni possono recapitare lo stesso evento
      if (state.items.some((i) => i.id === payload.notificationId)) return;

      const item = eventToItem(payload);
      commit("PREPEND_ITEM", item);
      commit("SET_UNREAD_COUNT", state.unreadCount + 1);

      // Primo evento della finestra: toast specifico subito + apertura finestra di accorpamento.
      // Eventi successivi entro la finestra: solo conteggiati, poi un unico toast riepilogativo.
      if (!toastWindowTimer) {
        showFailureToast(dispatch, item);
        toastBurstExtra = 0;
        toastWindowTimer = setTimeout(() => {
          const extra = toastBurstExtra;
          toastWindowTimer = null;
          toastBurstExtra = 0;
          if (extra > 0) showBurstSummaryToast(dispatch, extra);
        }, TOAST_BURST_WINDOW_MS);
      } else {
        toastBurstExtra += 1;
      }
    },
    async markAsRead({ commit }, notificationId) {
      await NotificationApi.markAsRead(notificationId);
      commit("MARK_READ", notificationId);
    },
    async markAllRead({ commit }) {
      await NotificationApi.markAllAsRead();
      commit("MARK_ALL_READ");
    },
    setConnectionStatus({ commit }, status) {
      commit("SET_CONNECTION_STATUS", status);
    },
    resetState({ commit }) {
      if (toastWindowTimer) {
        clearTimeout(toastWindowTimer);
        toastWindowTimer = null;
        toastBurstExtra = 0;
      }
      commit("RESET_STATE");
    },
  },
};
